import { useEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from "react"
import { createPortal } from "react-dom"
import { useDropdownMenu, type DropdownMenuDirection } from "@/components/ui/dropdown-menu-position"
import { cn } from "@/lib/utils"

const ITEM_SELECTOR = "[data-shell-menu-item]:not(:disabled)"

export const shellMenuItemClass = cn(
  "flex min-h-8 w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-xs text-ui-text-primary transition-colors outline-none",
  "hover:bg-ui-accent/25 focus-visible:bg-ui-accent/25 light:hover:bg-[var(--ui-nav-active-bg)] light:focus-visible:bg-[var(--ui-nav-active-bg)]",
)

export function ShellMenuTitle({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pt-1.5 pb-1 text-[10px] font-semibold tracking-wider text-ui-text-muted uppercase">
      {children}
    </div>
  )
}

export function ShellMenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-ui-border" />
}

type ShellMenuProps = {
  open: boolean
  onClose: () => void
  anchorRef: RefObject<HTMLElement | null>
  rootRef: RefObject<HTMLElement | null>
  width: number | "max-content"
  minWidth?: number
  maxWidth?: number
  direction: DropdownMenuDirection
  /** Горизонтальная привязка для `up` / `down`; для бокового flyout не используется. */
  align?: "left" | "right"
  ariaLabel: string
  positionKey?: unknown
  onEscape?: () => boolean
  children: ReactNode
}

export function ShellMenu({
  open,
  onClose,
  anchorRef,
  rootRef,
  width,
  minWidth,
  maxWidth,
  direction,
  align,
  ariaLabel,
  positionKey,
  onEscape,
  children,
}: ShellMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction,
    align,
    anchorRef,
    menuRef,
    rootRef,
    positionKey,
    resolveWidth: () => ({ width, minWidth, maxWidth: maxWidth ?? "calc(100vw - 16px)" }),
    onOutsideClick: onClose,
    estimatedMenuWidthPx: typeof width === "number" ? width : maxWidth,
  })
  const visible = menuStyle != null && menuStyle.visibility !== "hidden"

  useEffect(() => {
    if (!open || !visible) return
    const menu = menuRef.current
    if (!menu || menu.contains(document.activeElement)) return
    const autofocus = menu.querySelector<HTMLElement>("[data-shell-menu-autofocus] input")
    ;(autofocus ?? menu).focus({ preventScroll: true })
  }, [open, visible, menuStyle])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      if (onEscape?.()) return
      onClose()
      anchorRef.current?.focus()
      return
    }
    if (event.key === "Tab") {
      onClose()
      return
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? [])
    if (items.length === 0) return
    event.preventDefault()
    const index = items.indexOf(document.activeElement as HTMLElement)
    const next = event.key === "ArrowDown" ? (index + 1) % items.length : index <= 0 ? items.length - 1 : index - 1
    items[next]?.focus()
  }

  if (!open || !portalRoot || !menuStyle) return null

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={ariaLabel}
      tabIndex={-1}
      style={menuStyle}
      onKeyDown={onKeyDown}
      className="overflow-y-auto rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40 outline-none light:shadow-[var(--ui-shadow-md)]"
    >
      {children}
    </div>,
    portalRoot,
  )
}
